/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {useAuth} from '../../../app/modules/auth'
import Swal from 'sweetalert2'
import {LoadingSpinner} from '../../../utils/loading/loadingSpinner'
import {formatMonetaryToBr} from '../../../utils/formatMonetaryToBr'
import {Overview} from './Overview'
import {UsageByPeriod} from './UsageByPeriod'
import {InvoiceHistory} from './InvoiceHistory'
import {MyCard} from './myCard'
import {getFranchiseInvoiceCurrent} from '../core/_request'
import {creditCardResponse, franchiseInvoiceCurrentUsage} from '../core/_models'
import {formatarDataPtBr, getMesNome} from '../../../utils/dateMask'
import {getCreditCards} from './invoiceHistoryList/core/_requests'
import useAuthorization from '../../../hooks/useAuthorization'
import {ManualPaymentModal} from './ManualPaymentModal'
import {ChargeInvoiceModal} from './ChargeInvoiceModal'
import {TooltipWrapper} from '../../../components/Tooltip'

const INVOICE_STATUS_PENDING = 1
const INVOICE_STATUS_OVERDUE = 3

const ExpenseHeader: React.FC = () => {
  const {currentUser, currentFranchise} = useAuth()
  const [loadingCredtCardProgress, setLoadingCredtCardProgress] = useState(false)
  const [loadingCredtCardContentProgress, setLoadingCredtCardContentProgress] = useState(false)

  const [invoiceCurrentUsage, setInvoiceCurrentUsage] = useState<franchiseInvoiceCurrentUsage>()

  const [activeTab, setActiveTab] = useState('overview')

  const isAuthorizedToFranchiseInvoicesView = useAuthorization('franchiseInvoices.view')

  const isAuthorizedToFranchiseCreditCardsManage = useAuthorization('franchiseCreditCards.manage')

  const isAuthorizedToFranchiseInvoicesManualPayment = useAuthorization(
    'franchiseInvoices.manual_payment'
  )

  const isAuthorizedToFranchiseInvoicesCharge = useAuthorization('franchiseInvoices.charge')

  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false)

  const [showChargeInvoiceModal, setShowChargeInvoiceModal] = useState(false)

  const [hasCreditCards, setHasCreditCards] = useState(false)

  const [dataCreditCards, setDataCreditCards] = useState<creditCardResponse[]>([
    {
      id: 0,
      brand: {
        id: 0,
        description: '',
        created_at: '',
        updated_at: '',
      },
      expirationMonth: 0,
      expirationYear: 0,
      firstNumbers: 0,
      holderDocument: 0,
      holderName: '',
      lastNumbers: 0,
      token: 0,
      isFavorite: 0,
      createdAt: '',
      updatedAt: '',
    },
  ])

  function getStatusClass(status?: string): string {
    switch (status) {
      case 'Pendente':
        return 'btn btn-light-warning'
      case 'Pago':
        return 'btn btn-light-success'
      case 'Cancelado':
        return 'btn btn-light-danger'
      default:
        return 'btn btn-light-primary'
    }
  }

  const franchiseInvoiceCurrentUsageInit = async () => {
    try {
      setLoadingCredtCardProgress(true)
      setLoadingCredtCardContentProgress(true)
      const data = await getFranchiseInvoiceCurrent(currentFranchise?.id || 0)
      setInvoiceCurrentUsage(data)
      setHasCreditCards(!!data?.hasCreditCards)
    } catch (error: any) {
      await Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: error.response.data.message,
      })
    } finally {
      setLoadingCredtCardProgress(false)
      setLoadingCredtCardContentProgress(false)
    }
  }

  useEffect(() => {
    franchiseInvoiceCurrentUsageInit()
  }, [])

  // Mantém o estado sincronizado quando cartões são cadastrados/removidos na aba "Meus Cartões".
  // Ignora o placeholder inicial (id 0) usado antes da primeira carga.
  useEffect(() => {
    if (dataCreditCards.some((card) => card.id === 0)) return
    setHasCreditCards(dataCreditCards.length > 0)
  }, [dataCreditCards])

  const isInvoicePayable =
    invoiceCurrentUsage?.type === 'invoice' &&
    [INVOICE_STATUS_PENDING, INVOICE_STATUS_OVERDUE].includes(
      invoiceCurrentUsage?.invoice?.status?.id
    )

  const canMarkAsPaidManually = isAuthorizedToFranchiseInvoicesManualPayment && isInvoicePayable

  const canChargeInvoice = isAuthorizedToFranchiseInvoicesCharge && isInvoicePayable

  const getMyCreditCards = async () => {
    try {
      setLoadingCredtCardContentProgress(true)
      const data = await getCreditCards(currentFranchise?.id || 0)
      setDataCreditCards(data)
    } catch (error: any) {
      await Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: error.response.data.message,
      })
    } finally {
      setLoadingCredtCardContentProgress(false)
    }
  }
  return (
    <>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-body pt-9 pb-0'>
          {loadingCredtCardProgress ? (
            <LoadingSpinner
              title='Carregando'
              style={{
                width: '2rem',
                height: '2rem',
                colorSpinner: '#f1416c',
                colorText: '#f1416c',
              }}
            />
          ) : (
            <>
              <div className='mb-3'>
                <div className='row align-items-stretch'>
                  <div className='col-12 col-sm-6 mb-5 d-flex align-items-center justify-content-center'>
                    <div className='d-flex flex-column justify-content-between w-100'>
                      <div className='d-flex flex-column'>
                        <div className='d-flex align-items-center mb-2'>
                          Seu saldo devedor de
                          <span className='text-gray-800 text-hover-danger fs-2 fw-bolder me-1'>
                            &nbsp;
                            {getMesNome(
                              invoiceCurrentUsage?.type === 'invoice'
                                ? invoiceCurrentUsage.invoice?.referenceStart
                                : invoiceCurrentUsage?.usage?.referenceStart
                            )}
                          </span>
                        </div>

                        <div className='d-flex align-items-center mb-2'>
                          <span className='text-gray-800 text-hover-danger fs-2 fw-bolder me-1'>
                            {formatMonetaryToBr(
                              invoiceCurrentUsage?.type === 'invoice'
                                ? invoiceCurrentUsage.invoice?.totalAmount
                                : invoiceCurrentUsage?.usage?.totalAmount
                            )}
                          </span>
                        </div>

                        <div className='d-flex align-items-center mb-2'>
                          {invoiceCurrentUsage?.type === 'invoice'
                            ? `Este é o valor com base no seu consumo do mês. Referente a fatura atual: ${invoiceCurrentUsage.invoice?.referenceCode}.`
                            : 'Este é um valor estimado com base no seu consumo do mês. Ele pode variar até a data de vencimento da fatura.'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col-12 col-sm-6 mb-5 d-flex justify-content-end'>
                    <div className='d-flex flex-column align-items-end'>
                      {invoiceCurrentUsage?.type === 'invoice' && (
                        <>
                          <h1 className='me-1 py-3 w-300px'>
                            <a
                              className={`${getStatusClass(
                                invoiceCurrentUsage?.invoice?.status?.name
                              )}`}
                            >
                              Status: {invoiceCurrentUsage?.invoice.status.name}
                            </a>
                          </h1>
                          <div className='mb-3'>
                            <div className='border border-gray-300 border-dashed rounded py-3 px-4 w-300px'>
                              <div className='fs-2 fw-bolder'>
                                {formatarDataPtBr(invoiceCurrentUsage?.invoice?.dueDate)}
                              </div>
                              <div className='fw-bold fs-6 text-gray-400'>Data de vencimento</div>
                            </div>
                          </div>
                          {canChargeInvoice && (
                            <div className='mb-3'>
                              {hasCreditCards ? (
                                <button
                                  type='button'
                                  className='btn btn-light-primary w-300px'
                                  onClick={() => setShowChargeInvoiceModal(true)}
                                >
                                  <i className='ki-duotone ki-credit-cart fs-2'>
                                    <span className='path1'></span>
                                    <span className='path2'></span>
                                  </i>
                                  Cobrar no cartão
                                </button>
                              ) : (
                                <TooltipWrapper
                                  tooltipText='Nenhum cartão cadastrado para esta franquia'
                                  tooltipPlacement='top'
                                >
                                  <span className='d-inline-block'>
                                    <button
                                      type='button'
                                      className='btn btn-light-primary w-300px'
                                      disabled
                                    >
                                      <i className='ki-duotone ki-credit-cart fs-2'>
                                        <span className='path1'></span>
                                        <span className='path2'></span>
                                      </i>
                                      Cobrar no cartão
                                    </button>
                                  </span>
                                </TooltipWrapper>
                              )}
                            </div>
                          )}
                          {canMarkAsPaidManually && (
                            <div className='mb-3'>
                              <button
                                type='button'
                                className='btn btn-light-success w-300px'
                                onClick={() => setShowManualPaymentModal(true)}
                              >
                                <i className='ki-duotone ki-check-circle fs-2'>
                                  <span className='path1'></span>
                                  <span className='path2'></span>
                                </i>
                                Dar baixa manual
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      <div className='mb-3'>
                        <div className='border border-gray-300 border-dashed rounded py-3 px-4 w-300px'>
                          <div className='fs-2 fw-bolder'>
                            {invoiceCurrentUsage?.type === 'invoice'
                              ? invoiceCurrentUsage.invoice?.totalRequests
                              : invoiceCurrentUsage?.usage?.totalRequests}
                          </div>
                          <div className='fw-bold fs-6 text-gray-400'>Total de requisições</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='d-flex overflow-auto'>
                <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap'>
                  <li className='nav-item'>
                    <button
                      className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                      type='button'
                      onClick={() => setActiveTab('overview')}
                    >
                      Visão geral
                    </button>
                  </li>
                  {isAuthorizedToFranchiseInvoicesView && (
                    <li className='nav-item'>
                      <button
                        className={`nav-link ${activeTab === 'billing-history' ? 'active' : ''}`}
                        type='button'
                        onClick={() => setActiveTab('billing-history')}
                      >
                        Histórico de faturas
                      </button>
                    </li>
                  )}

                  <li className='nav-item'>
                    <button
                      className={`nav-link ${activeTab === 'usage-by-period' ? 'active' : ''}`}
                      type='button'
                      onClick={() => setActiveTab('usage-by-period')}
                    >
                      Consumo por período
                    </button>
                  </li>

                  {isAuthorizedToFranchiseCreditCardsManage && (
                    <li className='nav-item'>
                      <button
                        className={`nav-link ${activeTab === 'my-card' ? 'active' : ''}`}
                        type='button'
                        onClick={() => {
                          setActiveTab('my-card')
                          getMyCreditCards()
                        }}
                      >
                        Meus Cartões
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
      <div className='tab-content' id='pills-tabContent'>
        {activeTab === 'overview' && (
          <div className='tab-pane fade show active p-0 pt-0 pt-md-4'>
            <div className='card mb-5 mb-xl-10'>
              <div className='card-header border-0 d-flex justify-content-between align-items-center'>
                <div className='card-title m-0'>
                  <h3 className='fw-bolder m-0'>Visão geral</h3>
                </div>

                <div className='m-0'>
                  {invoiceCurrentUsage?.type === 'invoice'
                    ? `${formatarDataPtBr(
                        invoiceCurrentUsage.invoice.referenceStart
                      )} - ${formatarDataPtBr(invoiceCurrentUsage.invoice.referenceEnd)}`
                    : `${formatarDataPtBr(
                        invoiceCurrentUsage?.usage?.referenceStart
                      )} - ${formatarDataPtBr(invoiceCurrentUsage?.usage?.referenceEnd)}`}
                </div>
              </div>
              {loadingCredtCardContentProgress ? (
                <LoadingSpinner
                  title='Carregando'
                  style={{
                    width: '2rem',
                    height: '2rem',
                    colorSpinner: '#f1416c',
                    colorText: '#f1416c',
                  }}
                />
              ) : (
                <Overview invoiceCurrentUsage={invoiceCurrentUsage} />
              )}
            </div>
          </div>
        )}

        {isAuthorizedToFranchiseInvoicesView && activeTab === 'billing-history' && (
          <div className='tab-pane fade show active p-0 pt-0 pt-md-4'>
            <InvoiceHistory />
          </div>
        )}

        {activeTab === 'usage-by-period' && (
          <div className='tab-pane fade show active p-4 pt-0 pt-md-4'>
            <UsageByPeriod invoiceId={invoiceCurrentUsage?.invoice?.id} />
          </div>
        )}

        {isAuthorizedToFranchiseCreditCardsManage && activeTab === 'my-card' && (
          <div className='tab-pane fade show active p-4 pt-0 pt-md-4'>
            {loadingCredtCardContentProgress ? (
              <LoadingSpinner
                title='Carregando'
                style={{
                  width: '2rem',
                  height: '2rem',
                  colorSpinner: '#f1416c',
                  colorText: '#f1416c',
                }}
              />
            ) : (
              <MyCard
                dataCreditCards={dataCreditCards}
                franchiseId={currentFranchise?.id || 0}
                setDataCreditCards={setDataCreditCards}
                setLoadingCredtCardContentProgress={setLoadingCredtCardContentProgress}
              />
            )}
          </div>
        )}
      </div>

      {showManualPaymentModal && invoiceCurrentUsage?.type === 'invoice' && (
        <ManualPaymentModal
          franchiseId={currentFranchise?.id || 0}
          invoiceId={invoiceCurrentUsage.invoice.id}
          referenceCode={invoiceCurrentUsage.invoice.referenceCode}
          totalAmount={invoiceCurrentUsage.invoice.totalAmount}
          onClose={() => setShowManualPaymentModal(false)}
          onSuccess={() => {
            setShowManualPaymentModal(false)
            franchiseInvoiceCurrentUsageInit()
          }}
        />
      )}

      {showChargeInvoiceModal && invoiceCurrentUsage?.type === 'invoice' && (
        <ChargeInvoiceModal
          franchiseId={currentFranchise?.id || 0}
          invoiceId={invoiceCurrentUsage.invoice.id}
          referenceCode={invoiceCurrentUsage.invoice.referenceCode}
          totalAmount={invoiceCurrentUsage.invoice.totalAmount}
          canListCreditCards={isAuthorizedToFranchiseCreditCardsManage}
          onClose={() => setShowChargeInvoiceModal(false)}
          onSuccess={() => {
            setShowChargeInvoiceModal(false)
            franchiseInvoiceCurrentUsageInit()
          }}
        />
      )}
    </>
  )
}

export {ExpenseHeader}
